<!--
  MappingPreviewPerProduct.vue -- Phase 4.6 per-product card inside the
  BulkApplySetModal.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.6 BulkApplySetModal (full UI).

  One expandable card per selected campaign_product. The header shows
  the product name + garment family; the body renders one
  PlacementMapping row per placement_verdict and (in merge mode) one
  conflict resolution radio per conflict.

  Header click toggles expand/collapse; parent owns the open/closed
  state so the user's choices persist across mode toggles that re-fetch
  the preview.

  Variant routing placeholder note per ADR 0049 + P4-Q6 sits at the
  bottom of the body. Variant routing is APPLY-TIME (delta-E LAB per
  color SKU); this note tells the user it is automatic.

  Decoration Model Refactor P5 PR-F (Boulders #163,
  https://trello.com/c/S5kN5poU): adds the ambiguous-zone resolver
  row. When a recipe targets a zone-class with 2+ ACTIVE zones on the
  product (class_member_zones.length >= 2 on the verdict), the row
  surfaces a dropdown so the user can override the deterministic
  default (lowest-id zone). Selection emits a `remap` event on the
  per-placement set_placement_id with the chosen zone slug; the parent
  stores this in remapOverrides and the apply payload picks it up via
  the existing remap_overrides field. NO new backend contract is
  needed -- _resolve_target_zone already accepts slug overrides.
-->
<template>
    <div
        class="mapping-preview"
        data-testid="mapping-preview-product"
        :class="{ 'mapping-preview--expanded': expanded }"
    >
        <button
            type="button"
            class="mapping-preview__header"
            data-testid="mapping-preview-header"
            :aria-expanded="expanded ? 'true' : 'false'"
            @click="$emit('toggle-expanded')"
        >
            <span class="mapping-preview__caret">
                {{ expanded ? '▾' : '▸' }}
            </span>
            <span class="mapping-preview__name">
                {{ product.campaign_product_name }}
            </span>
            <span
                v-if="product.garment_family"
                class="mapping-preview__family"
            >
                ({{ product.garment_family }})
            </span>
        </button>
        <div
            v-if="expanded"
            class="mapping-preview__body"
            data-testid="mapping-preview-body"
        >
            <PlacementMapping
                v-for="v in product.placement_verdicts"
                :key="v.set_placement_id"
                :verdict="v"
                :remap-override="remapOverrideFor(v.set_placement_id)"
                :available-zones="availableZones"
                @remap="onRemap(v.set_placement_id, $event)"
            />
            <div
                v-if="mode === 'merge' && product.conflicts.length"
                class="mapping-preview__conflicts"
            >
                <p class="mapping-preview__conflicts-lede">
                    Conflicts already on this product:
                </p>
                <div
                    v-for="conflict in product.conflicts"
                    :key="conflict.existing_decoration_id"
                    class="mapping-preview__conflict"
                >
                    <span class="mapping-preview__conflict-text">
                        Existing decoration at
                        {{ conflict.existing_zone_slug }}
                    </span>
                    <label
                        class="mapping-preview__conflict-choice"
                        :data-testid="`mapping-preview-conflict-replace-${conflict.existing_decoration_id}`"
                        @click="
                            onResolveConflict(
                                conflict.existing_decoration_id,
                                'replace',
                            )
                        "
                    >
                        <input
                            type="radio"
                            :name="`conflict-${conflict.existing_decoration_id}`"
                            :checked="
                                conflictChoiceFor(
                                    conflict.existing_decoration_id,
                                ) === 'replace'
                            "
                        />
                        Replace existing
                    </label>
                    <label
                        class="mapping-preview__conflict-choice"
                        :data-testid="`mapping-preview-conflict-skip-${conflict.existing_decoration_id}`"
                        @click="
                            onResolveConflict(
                                conflict.existing_decoration_id,
                                'skip',
                            )
                        "
                    >
                        <input
                            type="radio"
                            :name="`conflict-${conflict.existing_decoration_id}`"
                            :checked="
                                conflictChoiceFor(
                                    conflict.existing_decoration_id,
                                ) === 'skip'
                            "
                        />
                        Skip new placement
                    </label>
                </div>
            </div>
            <p class="mapping-preview__variant-note">
                Variant routing auto-resolved at apply time (per-color
                SKU; delta-E LAB color match).
            </p>
        </div>
    </div>
</template>

<script>
import PlacementMapping from './PlacementMapping.vue'

export default {
    name: 'MappingPreviewPerProduct',
    components: { PlacementMapping },
    props: {
        product: {
            type: Object,
            required: true,
        },
        mode: {
            type: String,
            required: true,
        },
        remapOverrides: {
            type: Object,
            default: () => ({}),
        },
        conflictResolutions: {
            type: Object,
            default: () => ({}),
        },
        availableZones: {
            type: Array,
            default: () => [],
        },
        expanded: {
            type: Boolean,
            required: true,
            default: false,
        },
    },
    emits: ['toggle-expanded', 'remap', 'resolve-conflict'],
    methods: {
        remapOverrideFor(setPlacementId) {
            const key = String(setPlacementId)
            if (Object.prototype.hasOwnProperty.call(this.remapOverrides, key)) {
                return this.remapOverrides[key]
            }
            return undefined
        },
        conflictChoiceFor(existingDecorationId) {
            const key = String(existingDecorationId)
            return this.conflictResolutions[key]
        },
        onRemap(setPlacementId, zoneSlug) {
            this.$emit('remap', { setPlacementId, zoneSlug })
        },
        onResolveConflict(existingDecorationId, choice) {
            this.$emit('resolve-conflict', {
                existingDecorationId,
                choice,
            })
        },
    },
}
</script>

<style scoped>
.mapping-preview {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 8px;
    background: #fff;
}
.mapping-preview__header {
    width: 100%;
    text-align: left;
    background: #f8fafc;
    border: none;
    border-radius: 6px 6px 0 0;
    padding: 10px 12px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
}
.mapping-preview__caret {
    font-size: 12px;
    color: #64748b;
}
.mapping-preview__name {
    font-weight: 600;
    color: #1e293b;
}
.mapping-preview__family {
    color: #64748b;
    font-size: 12px;
}
.mapping-preview__body {
    padding: 12px;
}
.mapping-preview__conflicts {
    margin-top: 12px;
    padding: 8px;
    background: #fef3c7;
    border-radius: 4px;
}
.mapping-preview__conflicts-lede {
    margin: 0 0 6px;
    font-size: 12px;
    color: #78350f;
    font-weight: 600;
}
.mapping-preview__conflict {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: #1e293b;
    padding: 4px 0;
}
.mapping-preview__conflict-text {
    flex: 1 1 auto;
}
.mapping-preview__conflict-choice {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
}
.mapping-preview__variant-note {
    margin: 12px 0 0;
    font-size: 11px;
    color: #64748b;
    font-style: italic;
}
</style>
